// Members tab — org members table, invite form (admin only), role change, remove.

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import { ApiError } from "@/lib/api";
import {
  useInviteMember,
  useMembers,
  useRemoveMember,
  useUpdateMemberRole,
} from "@/hooks/useMembers";
import { useAuthStore } from "@/stores/authStore";
import { confirm } from "@/stores/confirmStore";
import { toast } from "@/stores/toastStore";
import { useDict, fmt } from "@/i18n/LocaleProvider";

const ROLE_COLORS: Record<string, string> = {
  admin: "rgb(var(--color-danger))",
  pentester: "rgb(var(--color-accent))",
  viewer: "rgb(var(--color-muted))",
  client: "rgb(var(--color-info))",
};

const ROLES = ["admin", "pentester", "viewer", "client"] as const;

function formatErr(err: unknown): string {
  if (err instanceof ApiError)
    return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
  if (err instanceof Error) return err.message;
  return String(err);
}

export function MembersTab() {
  const t = useDict().app.members;
  const user = useAuthStore((s) => s.user);
  const org = user?.organizations?.[0] ?? null;
  const isAdmin = org?.role === "admin";

  const members = useMembers(org?.id ?? null);
  const invite = useInviteMember(org?.id ?? "");
  const updateRole = useUpdateMemberRole(org?.id ?? "");
  const remove = useRemoveMember(org?.id ?? "");

  const [showInvite, setShowInvite] = useState(false);
  const [invEmail, setInvEmail] = useState("");
  const [invName, setInvName] = useState("");
  const [invRole, setInvRole] = useState<string>("pentester");
  const [invPwd, setInvPwd] = useState("");
  const [err, setErr] = useState<string | null>(null);

  if (!org) {
    return (
      <Card>
        <div className="text-dim text-center p-6 font-mono text-[11px]">
          {t.noOrg}
        </div>
      </Card>
    );
  }

  async function handleInvite() {
    setErr(null);
    try {
      await invite.mutateAsync({
        email: invEmail,
        full_name: invName || undefined,
        role: invRole,
        temporary_password: invPwd,
      });
      setShowInvite(false);
      setInvEmail("");
      setInvName("");
      setInvPwd("");
    } catch (e) {
      setErr(formatErr(e));
    }
  }

  async function handleChangeRole(userId: string, newRole: string) {
    try {
      await updateRole.mutateAsync({ userId, role: newRole });
      toast.success(t.roleUpdated);
    } catch (e) {
      toast.error(`Failed: ${formatErr(e)}`);
    }
  }

  async function handleRemove(userId: string, email: string) {
    const ok = await confirm({
      title: t.removeTitle,
      message: fmt(t.removeMsg, { email }),
      variant: "danger",
      confirmLabel: t.remove,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(userId);
      toast.success(t.memberRemoved);
    } catch (e) {
      toast.error(`Failed: ${formatErr(e)}`);
    }
  }

  const list = members.data ?? [];

  return (
    <div>
      {/* Header + invite button. Title and CTA stack on mobile so they
          don't collide; mb-6 below to give the invite form / table real
          breathing room rather than the previous 12px-tight spacing. */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <div className="font-display text-[16px] text-content font-semibold">
            {org.name} {t.membersSuffix}
          </div>
          <div className="font-mono text-[10px] text-muted mt-[2px]">
            {fmt(t.membersCount, { n: list.length })}
          </div>
        </div>
        {isAdmin && (
          <Button variant="primary" onClick={() => setShowInvite((v) => !v)}>
            {showInvite ? t.cancel : t.inviteMember}
          </Button>
        )}
      </div>

      {/* Invite form */}
      {isAdmin && showInvite && (
        <Card className="mb-6 border-accent">
          <div className="font-mono text-[11px] text-accent uppercase tracking-[0.08em] mb-3">
            {t.inviteNew}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label={t.email}
              value={invEmail}
              onChange={setInvEmail}
              placeholder={t.emailPlaceholder}
            />
            <Input
              label={t.fullNameOptional}
              value={invName}
              onChange={setInvName}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="mb-3">
              <label className="block mb-1 font-mono text-[11px] text-muted uppercase tracking-[0.05em]">
                {t.role}
              </label>
              <select
                value={invRole}
                onChange={(e) => setInvRole(e.target.value)}
                className="w-full px-3 py-3 md:py-2 min-h-[44px] md:min-h-[40px] bg-bg border border-stroke rounded text-content font-mono text-base md:text-[14px] outline-none"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t.roles[r]}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label={t.tempPassword}
              value={invPwd}
              onChange={setInvPwd}
              type="password"
              placeholder={t.tempPasswordPlaceholder}
            />
          </div>
          {err && (
            <div className="px-[10px] py-[6px] bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] mb-[10px]">
              {err}
            </div>
          )}
          <Button
            variant="primary"
            onClick={handleInvite}
            disabled={invite.isPending || !invEmail || !invPwd}
          >
            {invite.isPending ? t.busy : t.createMember}
          </Button>
        </Card>
      )}

      {/* Members list — desktop table from md+, stacked cards below.
          Header row uses the same py-3 + items-center as body rows so
          column titles vertically line up with row content (the legacy
          py-[10px] header sat above center-aligned rows and felt off). */}
      {members.isLoading ? (
        <Loading text={t.loading} />
      ) : (
        <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden flex flex-col gap-3">
            {list.map((m) => {
              const roleColor = ROLE_COLORS[m.role] ?? "rgb(var(--color-muted))";
              const canEdit = isAdmin && m.user_id !== user?.id;
              return (
                <Card key={m.membership_id} className="!p-4">
                  <div className="flex justify-between items-start gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-sans text-[14px] font-semibold text-content truncate">
                        {m.full_name || "—"}
                      </div>
                      <div className="font-mono text-[11px] text-muted truncate">
                        {m.email}
                      </div>
                    </div>
                    <span
                      className="px-2 py-[3px] rounded-[3px] text-[9px] font-bold uppercase tracking-[0.05em] shrink-0"
                      style={{
                        background: `${roleColor}22`,
                        border: `1px solid ${roleColor}66`,
                        color: roleColor,
                      }}
                    >
                      {t.roles[m.role as keyof typeof t.roles] ?? m.role}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px]">
                    <span style={{ color: m.is_active ? "rgb(var(--color-success))" : "rgb(var(--color-danger))" }}>
                      {m.is_active ? t.active : t.disabled}
                    </span>
                    <span className="text-muted">
                      {t.lastLogin}{" "}
                      {m.last_login_at
                        ? new Date(m.last_login_at).toLocaleDateString()
                        : t.never}
                    </span>
                  </div>
                  {canEdit && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <select
                        value={m.role}
                        onChange={(e) =>
                          handleChangeRole(m.user_id, e.target.value)
                        }
                        className="px-2 py-2 min-h-[36px] rounded font-mono text-[11px] uppercase cursor-pointer"
                        style={{
                          background: "rgb(var(--color-bg))",
                          border: `1px solid ${roleColor}66`,
                          color: roleColor,
                        }}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {t.roles[r]}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="default"
                        onClick={() => handleRemove(m.user_id, m.email)}
                        className="!text-danger !border-danger/40 hover:!border-danger/70"
                      >
                        {t.remove}
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Desktop: grid table */}
          <Card className="!p-0 overflow-hidden hidden md:block">
            <div
              className="grid gap-3 px-4 py-3 bg-bg2 border-b border-stroke font-mono text-[10px] text-muted uppercase tracking-[0.05em] font-semibold items-center"
              style={{
                gridTemplateColumns: "2fr 1fr 110px 120px 100px",
              }}
            >
              <div>{t.colUser}</div>
              <div>{t.colStatus}</div>
              <div>{t.colRole}</div>
              <div>{t.colLastLogin}</div>
              <div />
            </div>
            {list.map((m) => {
              const roleColor = ROLE_COLORS[m.role] ?? "rgb(var(--color-muted))";
              const canEdit = isAdmin && m.user_id !== user?.id;
              return (
                <div
                  key={m.membership_id}
                  className="grid gap-3 px-4 py-3 border-b border-stroke font-mono text-[11px] items-center"
                  style={{
                    gridTemplateColumns: "2fr 1fr 110px 120px 100px",
                  }}
                >
                  <div>
                    <div className="text-content font-semibold">
                      {m.full_name || "—"}
                    </div>
                    <div className="text-muted text-[10px] mt-[2px]">
                      {m.email}
                    </div>
                  </div>
                  <div>
                    <span
                      className="text-[10px]"
                      style={{ color: m.is_active ? "rgb(var(--color-success))" : "rgb(var(--color-danger))" }}
                    >
                      {m.is_active ? t.active : t.disabled}
                    </span>
                  </div>
                  <div>
                    {canEdit ? (
                      <select
                        value={m.role}
                        onChange={(e) =>
                          handleChangeRole(m.user_id, e.target.value)
                        }
                        className="px-[6px] py-[3px] rounded-[3px] font-mono text-[9px] font-bold uppercase cursor-pointer"
                        style={{
                          background: "rgb(var(--color-bg))",
                          border: `1px solid ${roleColor}66`,
                          color: roleColor,
                        }}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {t.roles[r]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className="px-2 py-[3px] rounded-[3px] text-[9px] font-bold uppercase tracking-[0.05em]"
                        style={{
                          background: `${roleColor}22`,
                          border: `1px solid ${roleColor}66`,
                          color: roleColor,
                        }}
                      >
                        {t.roles[m.role as keyof typeof t.roles] ?? m.role}
                      </span>
                    )}
                  </div>
                  <div className="text-muted text-[10px]">
                    {m.last_login_at
                      ? new Date(m.last_login_at).toLocaleDateString()
                      : t.never}
                  </div>
                  <div>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        small
                        onClick={() => handleRemove(m.user_id, m.email)}
                      >
                        {t.remove}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </Card>
        </>
      )}
    </div>
  );
}
